import { useEffect, useMemo, useState } from 'react';
import { Button, Input, Modal, Select, message } from 'antd';
import { useTranslation } from '../../../i18n';
import { useCurrentAccount } from '../layout/currentAccountContext';
import {
  addInfluencerBlacklist,
  cancelInfluencerCollection,
  getInfluencerCollectionPage,
} from '../../../store/request';

type ReasonType = 1 | 2 | 3 | 99;

type TalentOption = {
  value: string;
  label: string;
};

type AddBlacklistModalProps = {
  open: boolean;
  influencerId?: string;
  talentName?: string;
  onClose: () => void;
  onAdded?: (influencerId: string) => void;
};

export function AddBlacklistModal({
  open,
  influencerId,
  talentName,
  onClose,
  onAdded,
}: AddBlacklistModalProps) {
  const { t } = useTranslation();
  const account = useCurrentAccount();
  const lockedId = influencerId?.trim() || '';

  const reasonOptions = useMemo(
    () => [
      { value: 1 as ReasonType, label: t('creator.reason_timeout') },
      { value: 2 as ReasonType, label: t('creator.reason_no_update') },
      { value: 3 as ReasonType, label: t('creator.reason_fraud') },
      { value: 99 as ReasonType, label: t('creator.reason_other') },
    ],
    [t]
  );

  const [talentOptions, setTalentOptions] = useState<TalentOption[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [pickedId, setPickedId] = useState<string>();
  const [reasonType, setReasonType] = useState<ReasonType>(1);
  const [remark, setRemark] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setReasonType(1);
    setRemark('');
    setPickedId(lockedId || undefined);
    if (lockedId) {
      setTalentOptions([]);
      return;
    }
    const userCode = account?.userCode?.trim();
    if (!userCode) {
      setTalentOptions([]);
      return;
    }
    let cancelled = false;
    setOptionsLoading(true);
    getInfluencerCollectionPage({ userCode, pageNum: 1, pageSize: 50 })
      .then((res) => {
        if (cancelled) return;
        if (res.code !== 200) {
          setTalentOptions([]);
          return;
        }
        const options = (res.content?.records ?? [])
          .map((row) => {
            const id = row.influencerId?.trim();
            if (!id) return null;
            const name = row.nickname || row.username || id;
            return { value: id, label: name };
          })
          .filter((item): item is TalentOption => item != null);
        setTalentOptions(options);
      })
      .finally(() => {
        if (!cancelled) setOptionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, lockedId, account?.userCode]);

  const submit = async () => {
    const id = lockedId || pickedId?.trim() || '';
    if (!id) {
      message.warning(lockedId ? t('creator.blacklist_missing_id') : t('creator.select_talent_required'));
      return;
    }
    const note = remark.trim();
    if (note.length > 500) {
      return;
    }
    const talentLabel =
      talentName || talentOptions.find((item) => item.value === id)?.label || id;
    const reasonLabel = reasonOptions.find((item) => item.value === reasonType)?.label ?? '';
    setSubmitting(true);
    try {
      const res = await addInfluencerBlacklist({
        influencerId: id,
        reasonType,
        ...(note ? { remark: note } : {}),
      });
      if (res.code !== 200) {
        message.error(res.message || t('creator.blacklist_failed'));
        return;
      }
      try {
        await cancelInfluencerCollection(id);
      } catch {
        // 拉黑已成功；取消收藏失败时列表刷新后仍可能短暂残留，不阻断成功提示
      }
      message.success(res.message || t('creator.blacklist_added', { talent: talentLabel, reason: reasonLabel }));
      onAdded?.(id);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={t('creator.add_blacklist')}
      open={open}
      onCancel={onClose}
      destroyOnClose
      centered
      width={480}
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            type="primary"
            className="wb-btn-primary"
            loading={submitting}
            disabled={!lockedId && !pickedId}
            onClick={submit}
          >
            {t('creator.join')}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-24 shrink-0 text-right text-sm font-medium text-slate-700">
            {t('creator.col_talent')}
          </div>
          <div className="min-w-0 flex-1">
            {lockedId ? (
              <div className="text-sm text-slate-800">{talentName || lockedId}</div>
            ) : (
              <Select
                className="w-full"
                placeholder={t('creator.select_talent')}
                value={pickedId}
                options={talentOptions}
                loading={optionsLoading}
                onChange={setPickedId}
                showSearch
                optionFilterProp="label"
              />
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-24 shrink-0 text-right text-sm font-medium text-slate-700">
            {t('creator.reason')}
          </div>
          <Select
            className="min-w-0 flex-1"
            value={reasonType}
            options={reasonOptions}
            onChange={setReasonType}
          />
        </div>
        <div className="flex items-center gap-3">
          <div className="w-24 shrink-0 text-right text-sm font-medium text-slate-700">
            {t('creator.remark')}
          </div>
          <Input.TextArea
            className="min-w-0 flex-1"
            value={remark}
            maxLength={500}
            rows={3}
            onChange={(event) => setRemark(event.target.value)}
          />
        </div>
      </div>
    </Modal>
  );
}
